import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getCachedHikes, getCachedEntriesByHike } from '../utils/db';
import HikeMap from '../components/HikeMap';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// CSV Export Function
  const exportToCSV = (hikeData, entries) => {
    // Prepare CSV headers
    const headers = [
      'Date',
      'From',
      'To',
      'From Lat',
      'From Lng',
      'To Lat',
      'To Lng',
      'KM Travelled',
      'RPE (/10)',
      'Money Spent ($)',
      'Mood (/10)',
      'Sleep Quality (/10)',
      'Overall Feeling (/10)',
      'Calories Spent',
      'Weather Temp',
      'Weather Type',
      'Notes'
    ];

// Convert entries to CSV rows
const rows = entries.map(entry => [
  new Date(entry.date).toLocaleDateString(),
  entry.locationFrom?.name || '',
  entry.locationTo?.name || '',
  entry.locationFrom?.lat || '',
  entry.locationFrom?.lng || '',
  entry.locationTo?.lat || '',
  entry.locationTo?.lng || '',
  entry.kmTravelled,
  entry.rpe,
  entry.moneySpent,
  entry.mood,
  entry.sleepQuality,
  entry.overallFeeling,
  entry.caloriesSpent,
  entry.weatherTemp,
  entry.weatherType,
  `"${(entry.notes || '').replace(/"/g, '""')}"`
]);



    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${hikeData.name.replace(/\s+/g, '_')}_data.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };



function HikeReport() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [hikes, setHikes] = useState([]);
  const [selectedHikeId, setSelectedHikeId] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchHikes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedHikeId) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHikeId]);

const fetchHikes = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hikes`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch hikes');
    }

    const data = await response.json();
    setHikes(data);
    
    // Auto-select first hike if available
    if (data.length > 0 && !selectedHikeId) {
      setSelectedHikeId(data[0]._id);
    }
  } catch (err) {
    // Try to load from cache if offline
    const cachedHikes = await getCachedHikes();
    
    if (cachedHikes && cachedHikes.length > 0) {
      setHikes(cachedHikes);
      if (!selectedHikeId) {
        setSelectedHikeId(cachedHikes[0]._id);
      }
      setError(''); // Clear error if we have cached data
    } else {
      setError('Failed to load hikes. Please try again.');
    }
    console.error('Error fetching hikes:', err);
  }
};

const fetchStats = async () => {
  setLoading(true);
  setError('');
  
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/hikes/${selectedHikeId}/stats`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch statistics');
    }

    const data = await response.json();
    setStats(data);
  } catch (err) {
    console.error('Error fetching stats:', err);
    
    // Try to load from cache if offline
    try {
      const cachedHikes = await getCachedHikes();
      const cachedHike = cachedHikes.find(h => h._id === selectedHikeId);
      const cachedEntries = await getCachedEntriesByHike(selectedHikeId);
      
      if (cachedHike) {
        // Calculate stats from cached data
        const entries = cachedEntries || [];
        const totalKmTravelled = entries.reduce((sum, entry) => sum + (entry.kmTravelled || 0), 0);
        const totalMoneySpent = entries.reduce((sum, entry) => sum + (entry.moneySpent || 0), 0);
        const entriesWithDistance = entries.filter(entry => entry.kmTravelled > 0).length;
        
        // Calculate category totals
        const categoryTotals = {
          Food: 0,
          Accommodation: 0,
          Travel: 0,
          Equipment: 0,
          General: 0,
          'Pre-Hike': 0
        };
        
        entries.forEach(entry => {
          if (entry.expenses && Array.isArray(entry.expenses)) {
            entry.expenses.forEach(expense => {
              if (categoryTotals.hasOwnProperty(expense.category)) {
                categoryTotals[expense.category] += expense.amount;
              }
            });
          }
        });
        
        const preHikeSpent = categoryTotals['Pre-Hike'];
        const onTrailSpent = totalMoneySpent - preHikeSpent;
        
        setStats({
          hike: cachedHike,
          entries: entries,
          stats: {
            totalKmTravelled,
            totalMoneySpent,
            preHikeSpent,
            onTrailSpent,
            categoryTotals,
            distanceRemaining: Math.max(0, cachedHike.totalDistance - totalKmTravelled),
            preHikeBudgetRemaining: Math.max(0, (cachedHike.preHikeBudget || 0) - preHikeSpent),
            onTrailBudgetRemaining: Math.max(0, (cachedHike.onTrailBudget || cachedHike.budget) - onTrailSpent),
            distancePercentage: cachedHike.totalDistance > 0 
              ? ((totalKmTravelled / cachedHike.totalDistance) * 100).toFixed(1) : 0,
            preHikeBudgetPercentage: (cachedHike.preHikeBudget || 0) > 0
              ? ((preHikeSpent / cachedHike.preHikeBudget) * 100).toFixed(1) : 0,
            onTrailBudgetPercentage: (cachedHike.onTrailBudget || cachedHike.budget) > 0 
              ? ((onTrailSpent / (cachedHike.onTrailBudget || cachedHike.budget)) * 100).toFixed(1) : 0,
            totalEntries: entries.length,
            entriesWithDistance
          }
        });
        setError('');
      } else {
        setError('No cached data available. Please connect to the internet.');
      }
    } catch (cacheErr) {
      console.error('Error loading cached stats:', cacheErr);
      setError('Failed to load statistics.');
    }
  } finally {
    setLoading(false);
  }
};

  const deleteEntry = async (entryId, hikeId) => {
  if (!window.confirm('Are you sure you want to delete this entry? This cannot be undone.')) {
    return;
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/hikes/${hikeId}/entries/${entryId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete entry');
    }

    fetchStats();
    alert('Entry deleted successfully!');
  } catch (err) {
    console.error('Error deleting entry:', err);
    alert('Failed to delete entry. Please try again.');
  }
};

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  };

  const prepareChartData = () => {
    if (!stats || !stats.entries || stats.entries.length === 0) {
      return [];
    }

    // Sort entries by date in ascending order (oldest first)
    const sortedEntries = [...stats.entries].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calculate cumulative values
    let cumulativeKm = 0;
    let cumulativeMoney = 0;

    return sortedEntries.map(entry => {
      cumulativeKm += entry.kmTravelled;
      cumulativeMoney += entry.moneySpent;

      return {
        date: formatDate(entry.date),
        kmTravelled: entry.kmTravelled,
        cumulativeKm: cumulativeKm,
        totalDistanceTarget: stats.hike.totalDistance, // Flat line for total distance
        rpe: entry.rpe,
        moneySpent: entry.moneySpent,
        cumulativeMoney: cumulativeMoney,
        budgetTarget: stats.hike.budget, // Flat line for budget
        mood: entry.mood,
        sleepQuality: entry.sleepQuality,
        overallFeeling: entry.overallFeeling,
        caloriesSpent: entry.caloriesSpent
      };
    });
  };

  if (!user) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Please log in to view hike reports.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Hike Report</h1>
        <div className="space-x-2">
          {stats && stats.entries && stats.entries.length > 0 && (
            <button
              onClick={() => exportToCSV(stats.hike, stats.entries)}
              className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded"
            >
              📥 Export CSV
            </button>
          )}
          <button
            onClick={() => navigate('/hike/update')}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Add Entry
          </button>
          <button
            onClick={() => navigate('/hike/setup')}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            New Hike
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {hikes.length === 0 ? (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">No hikes found</p>
          <p className="text-sm">Create your first hike to start tracking your journey!</p>
          <button
            onClick={() => navigate('/hike/setup')}
            className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
          >
            Setup Your First Hike
          </button>
        </div>
      ) : (
        <>
          {/* Hike Selection */}
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="hikeSelect">
              Select Hike
            </label>
            <select
              id="hikeSelect"
              value={selectedHikeId}
              onChange={(e) => setSelectedHikeId(e.target.value)}
              className="shadow border rounded w-full md:w-1/2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            >
              {hikes.map(hike => (
                <option key={hike._id} value={hike._id}>
                  {hike.name}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading statistics...</p>
            </div>
          ) : stats ? (
            <>
              {/* Summary Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {/* Distance Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Distance</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {stats.stats.totalKmTravelled.toFixed(1)} km
                  </div>
                  <p className="text-sm text-gray-600">
                    of {stats.hike.totalDistance} km ({stats.stats.distancePercentage}%)
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.min(stats.stats.distancePercentage, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Remaining: {stats.stats.distanceRemaining.toFixed(1)} km
                  </p>
                </div>

                {/* Pre-Hike Budget Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Pre-Hike Budget</h3>
                  <div className="text-3xl font-bold text-purple-600 mb-1">
                    ${(stats.stats.preHikeSpent || 0).toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-600">
                    of ${(stats.hike.preHikeBudget || 0).toFixed(2)} ({stats.stats.preHikeBudgetPercentage || 0}%)
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${Math.min(stats.stats.preHikeBudgetPercentage || 0, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Remaining: ${(stats.stats.preHikeBudgetRemaining || 0).toFixed(2)}
                  </p>
                </div>

                {/* On-Trail Budget Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">On-Trail Budget</h3>
                  <div className="text-3xl font-bold text-green-600 mb-1">
                    ${(stats.stats.onTrailSpent || 0).toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-600">
                    of ${(stats.hike.onTrailBudget || stats.hike.budget).toFixed(2)} ({stats.stats.onTrailBudgetPercentage || 0}%)
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${Math.min(stats.stats.onTrailBudgetPercentage || 0, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Remaining: ${(stats.stats.onTrailBudgetRemaining || 0).toFixed(2)}
                  </p>
                </div>

                {/* Total Spending Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Spending</h3>
                  <div className="text-3xl font-bold text-orange-600 mb-1">
                    ${stats.stats.totalMoneySpent.toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-600">
                    Pre-Hike + On-Trail combined
                  </p>
                </div>

                {/* Entries Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Entries</h3>
                  <div className="text-3xl font-bold text-indigo-600 mb-1">
                    {stats.stats.totalEntries}
                  </div>
                  <p className="text-sm text-gray-600">
                    Days logged
                  </p>
                </div>

                {/* Daily Average Card */}
                <div className="bg-white shadow-md rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Daily Avg</h3>
                  <div className="text-xl font-bold text-cyan-600 mb-1">
                    {(stats.stats.entriesWithDistance || 0) > 0
                      ? (stats.stats.totalKmTravelled / stats.stats.entriesWithDistance).toFixed(1)
                      : '0'} km
                  </div>
                  <p className="text-sm text-gray-600 mb-1">Distance per day</p>
                  <div className="text-xl font-bold text-cyan-600">
                    ${stats.stats.totalEntries > 0
                      ? ((stats.stats.onTrailSpent || 0) / stats.stats.totalEntries).toFixed(2)
                      : '0'}
                  </div>
                  <p className="text-sm text-gray-600">On-trail spending per day</p>
                </div>
              </div>
              
              {/* Spending by Category */}
              <div className="bg-white shadow-md rounded-lg p-6 mb-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Spending by Category</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {stats.stats.categoryTotals && Object.entries(stats.stats.categoryTotals).map(([category, amount]) => (
                    <div key={category} className="border rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-600">{category}</div>
                      <div className="text-2xl font-bold text-gray-900">${amount.toFixed(2)}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {stats.stats.totalMoneySpent > 0 
                          ? ((amount / stats.stats.totalMoneySpent) * 100).toFixed(1) 
                          : '0'}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Trail Map */}
              {stats.entries && stats.entries.length > 0 && (
                <HikeMap entries={stats.entries} />
              )}
              {/* Charts */}
              {stats.entries.length > 0 ? (
                <div className="space-y-8">
                  {/* Distance Chart - Cumulative with Target Line */}
                  <div className="bg-white shadow-md rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Cumulative Distance Travelled (km)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={prepareChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 'auto']} />
                        <Tooltip />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                        <Line type="monotone" dataKey="cumulativeKm" stroke="#2563eb" strokeWidth={2} name="Cumulative KM" />
                        <Line type="monotone" dataKey="totalDistanceTarget" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" name="Total Distance Goal" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Money Spent Chart - Cumulative with Budget Line */}
                  <div className="bg-white shadow-md rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Cumulative Money Spent ($)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={prepareChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 'auto']} />
                        <Tooltip />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                        <Line type="monotone" dataKey="cumulativeMoney" stroke="#16a34a" strokeWidth={2} name="Cumulative $ Spent" />
                        <Line type="monotone" dataKey="budgetTarget" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" name="Total Budget" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Calories Chart */}
                  <div className="bg-white shadow-md rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Calories Spent</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={prepareChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 'auto']} />
                        <Tooltip />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                        <Line type="monotone" dataKey="caloriesSpent" stroke="#ea580c" strokeWidth={2} name="Calories" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Well-being Chart - Including RPE */}
                  <div className="bg-white shadow-md rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Well-being & Exertion Indicators (/10)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={prepareChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 10]} />
                        <Tooltip />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                        <Line type="monotone" dataKey="rpe" stroke="#dc2626" strokeWidth={2} name="RPE (Exertion)" />
                        <Line type="monotone" dataKey="mood" stroke="#8b5cf6" strokeWidth={2} name="Mood" />
                        <Line type="monotone" dataKey="sleepQuality" stroke="#0891b2" strokeWidth={2} name="Sleep Quality" />
                        <Line type="monotone" dataKey="overallFeeling" stroke="#22c55e" strokeWidth={2} name="Overall Feeling" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                    {/* Daily Notes/Reflections Section */}
                    {stats.entries && stats.entries.length > 0 && (
                      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h3 className="text-2xl font-bold mb-4">📝 Daily Notes & Reflections</h3>
                        <div className="space-y-4">
                          {stats.entries
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((entry, index) => (
                              <div 
                                key={index} 
                                className="border-l-4 border-green-500 pl-4 py-2 bg-gray-50 rounded-r"
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1">
                                    <div className="font-bold text-gray-700">
                                      {new Date(entry.date).toLocaleDateString('en-AU', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                      })}
                                    </div>
                                    {(entry.locationFrom?.name || entry.locationTo?.name) && (
                                      <div className="text-sm text-gray-600 mt-1">
                                        📍 {entry.locationFrom?.name || '-'} → {entry.locationTo?.name || '-'}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="text-sm text-gray-600">
                                      {entry.kmTravelled} km
                                    </div>
                                    <button
                                      onClick={() => navigate(`/hike/update?date=${entry.date.split('T')[0]}&hikeId=${entry.hikeId}`)}
                                      className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold py-1 px-3 rounded transition-colors"
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button
                                      onClick={() => deleteEntry(entry._id, entry.hikeId)}
                                      className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold py-1 px-3 rounded transition-colors"
                                    >
                                      🗑️ Delete
                                    </button>
                                  </div>
                                </div>
                                <p className="text-gray-700 whitespace-pre-wrap">
                                  {entry.notes}
                                </p>
                                <div className="mt-2 text-xs text-gray-500 flex gap-4">
                                  <span>😊 Mood: {entry.mood}/10</span>
                                  <span>💪 RPE: {entry.rpe}/10</span>
                                  <span>😴 Sleep: {entry.sleepQuality}/10</span>
                                </div>
                              </div>
                            ))}
                          
                          {stats.entries.length === 0 && (
                            <p className="text-gray-500 italic">
                              No entries yet. Start logging your hike from the Update Hike page!
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                                      {/* Manage All Entries Section */}
                  {stats.entries && stats.entries.length > 0 && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                      <h3 className="text-2xl font-bold mb-4">📋 Manage All Entries</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distance (km)</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">$ Spent</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RPE</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mood</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {stats.entries
                              .sort((a, b) => new Date(b.date) - new Date(a.date))
                              .map((entry, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {new Date(entry.date).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {entry.kmTravelled} km
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ${entry.moneySpent.toFixed(2)}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {entry.rpe}/10
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {entry.mood}/10
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                    <button
                                      onClick={() => navigate(`/hike/update?date=${entry.date.split('T')[0]}&hikeId=${entry.hikeId}`)}
                                      className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold py-1 px-3 rounded transition-colors"
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button
                                      onClick={() => deleteEntry(entry._id, entry.hikeId)}
                                      className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold py-1 px-3 rounded transition-colors"
                                    >
                                      🗑️ Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="text-sm text-gray-700">
                    <strong>No entries yet!</strong> Add your first entry to start seeing trends and statistics.
                  </p>
                </div>
              )}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export default HikeReport;